import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Bell } from 'lucide-react';
import md5 from 'blueimp-md5';
import { getNotifications, markNotificationRead } from '../utils/api';
import logo from '../assest/l.png'

export default function MatrimonyHeader() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [userName, setUserName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [gravatarUrl, setGravatarUrl] = useState(null);

  useEffect(() => {
    const name = localStorage.getItem('matrimonyUserName');
    const email = localStorage.getItem('matrimonyUserEmail');
    setUserName(name);
    setUserEmail(email);

    if (email) {
      const hash = md5(email.trim().toLowerCase());
      setGravatarUrl(`https://www.gravatar.com/avatar/${hash}?d=identicon`);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!dropdownOpen && !notifOpen) return;
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('matrimony-dropdown');
      const notifDropdown = document.getElementById('notification-dropdown');
      if (
        (dropdown && !dropdown.contains(event.target)) &&
        (notifDropdown && !notifDropdown.contains(event.target))
      ) {
        setDropdownOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen, notifOpen]);

  useEffect(() => {
    const userId = localStorage.getItem('matrimonyUserId');
    if (userId) {
      getNotifications(userId)
        .then((data) => {
          setNotifications(data.notifications || []);
        })
        .catch((err) => console.error('Failed to load notifications', err));
    }
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/matrimony-login');
  };

  const handleNotificationClick = async (notif) => {
    await markNotificationRead(notif._id);
    setNotifOpen(false);
    navigate('/request');
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 px-10 py-4 transition-all duration-300 ${isScrolled ? 'bg-white/50 backdrop-blur-md text-black shadow' : 'bg-transparent text-white'
        }`}
    >
      {/* Common Container */}
      <div className="flex justify-between items-center">
        {/* Logo on Left */}
        <Link to="/" className="flex-shrink-0">
          <img src={logo} alt="Logo" className="h-8 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex justify-center items-center flex-grow">
          <nav className="flex gap-6">
            <Link
              to="/matrimony"
              className={`hover:underline ${isScrolled ? 'hover:text-orange-600' : 'hover:text-orange-300'
                }`}
            >
              Home
            </Link>
            <Link
              to="/matrimony-profile"
              className={`hover:underline ${isScrolled ? 'hover:text-orange-600' : 'hover:text-orange-300'
                }`}
            >
              All Profiles
            </Link>
            <Link
              to="/matrimony-pricing"
              className={`hover:underline ${isScrolled ? 'hover:text-orange-600' : 'hover:text-orange-300'
                }`}
            >
              Pricing
            </Link>
          </nav>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {userName ? (
            <>
              {/* Notification */}
              <div className="relative" id="notification-dropdown">
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className={`relative ${isScrolled ? 'text-black' : 'text-white'}`}
                >
                  <Bell className="w-6 h-6" />
                  {notifications.some((n) => !n.isRead) && (
                    <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white text-black rounded shadow-lg max-h-64 overflow-y-auto z-50 px-4">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500">No notifications</p>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif._id}
                          onClick={() => handleNotificationClick(notif)}
                          className="cursor-pointer hover:bg-gray-100 p-2"
                        >
                          {notif.message}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div className="relative ml-4" id="matrimony-dropdown">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2"
                >
                  <img
                    src={gravatarUrl || '/m_header.jpg'}
                    alt="Profile"
                    className="rounded-full w-8 h-8 border border-gray-300"
                  />
                  <span className="text-base">{userName}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white text-black rounded-lg shadow-lg py-2 z-50">
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate('/dashboard');
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Dashboard
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <Link
                to="/matrimony-login"
                className={`px-4 py-2 rounded ${isScrolled ? 'bg-orange-500 text-white' : 'bg-orange-500 text-white'
                  }`}
              >
                Login
              </Link>
              <Link
                to="/matrimony-register"
                className={`px-4 py-2 rounded ${isScrolled ? 'bg-pink-500 text-white' : 'bg-pink-500 text-white'
                  }`}
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="flex md:hidden ml-auto">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={isScrolled ? 'text-black' : 'text-white'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div
          className={`md:hidden mt-4 flex flex-col gap-4 px-4 ${isScrolled ? 'bg-white/90 text-black rounded shadow' : 'bg-black/80 text-white rounded'
            } py-4`}
        >
          <Link
            to="/all-profiles"
            className={`hover:underline ${isScrolled ? 'hover:text-orange-600' : 'hover:text-orange-300'
              }`}
          >
            All Profiles
          </Link>
          <Link
            to="/matrimony-pricing"
            className={`hover:underline ${isScrolled ? 'hover:text-orange-600' : 'hover:text-orange-300'
              }`}
          >
            Pricing
          </Link>
          {userName ? (
            <>
              <div className="flex items-center gap-2">
                <img
                  src={gravatarUrl || '/m_header.jpg'}
                  alt="Profile"
                  className="rounded-full w-8 h-8 border border-gray-300"
                />
                <span className="text-base">{userName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="text-left px-4 py-2 hover:bg-gray-100"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/matrimony-login"
                className={`px-4 py-2 rounded ${isScrolled ? 'bg-orange-500 text-white' : 'bg-orange-500 text-white'
                  } text-center`}
              >
                Login
              </Link>
              <Link
                to="/matrimony-register"
                className={`px-4 py-2 rounded ${isScrolled ? 'bg-pink-500 text-white' : 'bg-pink-500 text-white'
                  } text-center`}
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}